import { createFileRoute } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronsUpDown } from 'lucide-react';
import { ChangeEvent, Suspense, useEffect, useState } from 'react';
import { z } from 'zod';

import { Route as AdminIngredientRoute } from '@/app/routes/_authenticated/admin/suppliers/$supplierId/$ingredientId';
import { Button } from '@/components/ui/button/button';
import { DataTable } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ViewContent, ViewHeader } from '@/components/view-layout';
import { useSupplierIngredients } from '@/features/ingredients/api/ingredients-queries';
import { AddManagedIngredientModal } from '@/features/ingredients/components/add-managed-ingredient-modal';
import { Ingredient } from '@/features/ingredients/types/ingredient-types';
import { useSupplier } from '@/features/suppliers/api/supplier-queries';

const columnHelper = createColumnHelper<Ingredient>();

const columns = [
  columnHelper.accessor('code', {
    header: 'Code',
    cell: (info) => info.getValue(),
    size: 20,
    filterFn: 'includesString',
  }),
  columnHelper.accessor('title', {
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-[110px] px-0 justify-start"
        >
          Name
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: (info) => info.getValue(),
    size: 40,
  }),
];

interface IngredientsTableProps {
  supplierId: string;
  searchQuery: string;
}

function IngredientsTable({ supplierId, searchQuery }: IngredientsTableProps) {
  const { data: ingredients } = useSupplierIngredients(supplierId);
  const navigate = Route.useNavigate();

  const table = useReactTable({
    data: ingredients,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  useEffect(() => {
    table.setGlobalFilter(searchQuery);
  }, [searchQuery, table]);

  const handleRowClick = async (ingredient: Ingredient) => {
    await navigate({
      to: AdminIngredientRoute.to,
      params: {
        supplierId,
        ingredientId: ingredient.id,
      },
    });
  };

  return <DataTable table={table} onClick={handleRowClick} />;
}

interface AdminIngredientListContentProps {
  supplierId: string;
}

export function AdminIngredientListContent({
  supplierId,
}: AdminIngredientListContentProps) {
  const { data: supplier } = useSupplier(supplierId);

  const [showAddModal, setShowAddModal] = useState(false);
  const { search } = Route.useSearch();
  const navigate = Route.useNavigate();

  const table = useReactTable({
    data: [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleImportCSV = () => {
    // TODO: Implement CSV import logic here
  };

  const handleSearchChange = async (e: ChangeEvent<HTMLInputElement>) => {
    await navigate({
      search: {
        search: e.target.value,
      },
    });
  };

  return (
    <>
      <ViewHeader title={`${supplier.title} Ingredients`}>
        <div className="flex gap-4 items-center">
          <Input
            placeholder="Search by code or name"
            value={search}
            onChange={handleSearchChange}
          />
          <div className="flex items-center">
            <div className="inline-flex items-center border rounded-lg overflow-hidden">
              <Button
                onClick={() => setShowAddModal(true)}
                className="rounded-r-none"
              >
                Add ingredient
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" className="rounded-l-none">
                    <ChevronDown className="h-4 w-4 text-primary-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleImportCSV}>
                    Import CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </ViewHeader>
      <ViewContent>
        <Suspense
          fallback={<DataTable table={table} loading={true} skeletonRows={5} />}
        >
          <IngredientsTable supplierId={supplierId} searchQuery={search} />
        </Suspense>
      </ViewContent>
      <AddManagedIngredientModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        supplierId={supplierId}
      />
    </>
  );
}

export function AdminIngredientListPage() {
  const { supplierId } = Route.useParams();

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <AdminIngredientListContent supplierId={supplierId} />
    </Suspense>
  );
}

const adminIngredientListPageSearchSchema = z.object({
  search: z.string().default('').catch(''),
});

export const Route = createFileRoute(
  '/_authenticated/admin/suppliers/$supplierId/',
)({
  validateSearch: adminIngredientListPageSearchSchema,
  component: AdminIngredientListPage,
});
